import { useQuery } from '@apollo/client/react'
import { TrainerCapability } from '@zeus/index'
import { FileDown, Loader } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { StatTile } from '~/components/StatTile'
import { Button } from '~/components/ui/button'
import { GQL_BACK_OFFICE_HOME } from '~/gql/home/home'
import { GQL_SCORM_TRACKING_STATS } from '~/gql/scorm/scorm'
import { client } from '~/helpers/apollo'
import { brandBackdrop, TEXT_SAFE_TINT } from '~/helpers/branding/brand-backdrop'
import { tourAnchor } from '~/helpers/tour/anchors'
import { useCan } from '~/hooks/use-can'
import { useReportDownload } from '~/hooks/use-report-download'
import { cn } from '~/lib/utils'
import CompletionBreakdown from '~/pages/backoffice/admin/dashboard/CompletionBreakdown'
import { statTiles } from '~/pages/backoffice/admin/dashboard/stat-tiles'

/**
 * Synthèse d'avancement du tableau de bord (#138).
 *
 * Répond à « où en est ma promo ? », question à laquelle la table en dessous ne
 * répond pas : elle décrit un apprenant à la fois, et sa page courante ne fait
 * jamais que dix lignes. Les chiffres viennent donc d'une agrégation serveur sur
 * tout le périmètre filtré.
 *
 * ⚠️ Aucune variation « +12 % » nulle part, contrairement aux maquettes de
 * tableau de bord habituelles : l'API rend un instantané, pas une série. Un
 * delta suppose une période précédente que personne ne calcule ici — l'afficher
 * reviendrait à inventer un chiffre. Le jour où une série existe, la tuile a
 * déjà sa place pour lui (`hint`).
 */
function CompletionSummary() {
  const { t } = useTranslation()

  // Aucun filtre, et c'est le point : ces chiffres décrivent TOUTE
  // l'organisation. Branchés sur les filtres de la table, ils changeaient sous
  // les yeux du lecteur dès qu'il cherchait un apprenant — un même écran
  // annonçait alors deux « taux de complétion » différents selon ce qu'on avait
  // tapé, sans que rien ne dise lequel valait pour l'organisme.
  const { data, previousData } = useQuery(GQL_SCORM_TRACKING_STATS, {
    client,
    fetchPolicy: 'cache-and-network',
    variables: { input: {} },
  })

  // Même requête que `AlertStrip`, servie par le cache : seul le prénom sert ici.
  const { data: homeData, previousData: homePrevious } = useQuery(GQL_BACK_OFFICE_HOME, {
    fetchPolicy: 'cache-and-network',
  })
  const home = homeData?.backOfficeHome ?? homePrevious?.backOfficeHome
  const canExport = useCan(TrainerCapability.STATISTICS_EXPORT)

  /**
   * Rapport d'activité PDF (#12).
   *
   * `fetch` et non une mutation GraphQL : la réponse est un binaire. Le passer
   * en base64 dans une réponse GraphQL doublerait son poids et obligerait à le
   * reconstruire côté navigateur pour rien.
   *
   * Le nom du fichier vient de l'en-tête `Content-Disposition` (cf.
   * `downloadFromApi`).
   */
  const { download: downloadReport, downloading } = useReportDownload('/statistics/report', 'rapport.pdf')

  // Comme la table : on garde l'affichage précédent pendant le chargement,
  // plutôt que de faire clignoter des zéros qui se liraient comme une réalité.
  const stats = data?.getScormTrackingStats ?? previousData?.getScormTrackingStats
  const total = stats?.total ?? 0

  const tiles = statTiles(stats, t)

  // Sans inscrit, l'illustration n'accompagne aucun chiffre : elle laisserait un
  // grand vide à côté d'une seule phrase (#106).
  const showsIllustration = total > 0

  return (
    <section className="relative" {...tourAnchor('dashboard.summary')}>
      {/*
        Bandeau au fond de marque, le même motif que la page de connexion (#79).
        Du texte y est posé, gris secondaire compris : il prend donc l'intensité
        `TEXT_SAFE_TINT`, dont les plafonds sont épinglés pour garder ce texte
        AA quelle que soit la marque, en clair comme en sombre.
      */}
      {/*
        Pas de `relative` ici, volontairement : le référent de position de
        l'illustration est la <section>, pour que sa coupe tombe sous les tuiles.

        ⚠️ La section n'a plus de marge haute où l'illustration pourrait
        déborder : sa fenêtre a une hauteur fixe, ancrée en bas, et un bandeau
        court la faisait sortir par le haut, tête coupée (#106). Quand elle est
        affichée, la carte fait donc au moins la hauteur de cette fenêtre — les
        tuiles qui la chevauchent compensent le décalage du bas.
      */}
      <div
        className={cn('rounded-3xl px-6 pt-8 pb-28 sm:px-8', showsIllustration && 'xl:min-h-[23rem] 2xl:min-h-[27rem]')}
        style={{ background: brandBackdrop(TEXT_SAFE_TINT) }}
      >
        <div className="flex items-start justify-between gap-6">
          {/* `flex-1` : sans lui, la colonne se réduisait à son contenu et
              « Rapport PDF » se collait au titre au lieu de partir à droite. */}
          <div className="flex min-w-0 flex-1 flex-col gap-5 xl:pr-[21rem] 2xl:pr-[24rem]">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex flex-col gap-1">
                <h2 className="text-2xl font-semibold tracking-tight">
                  {t('backHome.greeting', { name: home?.firstName ?? '' })}
                </h2>
                <p className="text-sm text-muted-foreground">{t('dashboard.subtitle')}</p>
              </div>
              {canExport && (
                <Button variant="outline" onClick={downloadReport} disabled={downloading} {...tourAnchor('dashboard.report')}>
                  {downloading
                    ? <Loader className="mr-2 size-4 animate-spin" />
                    : <FileDown className="mr-2 size-4" />}
                  {t('stats.report.download')}
                </Button>
              )}
            </div>

            <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('stats.dashboard.scopeTitle')}
            </h2>

            <CompletionBreakdown
              stats={stats}
              scopeLabel={t('stats.dashboard.scope')}
              notes={!!stats?.neverOpened && (
                // L'écart assumé avec la table du tableau de bord : une ligne de
                // progression n'existe qu'au premier lancement du module, donc ces
                // inscrits comptent dans l'effectif sans y figurer. La fiche
                // session, elle, liste tous ses inscrits : la note n'y a pas lieu.
                <p className="text-xs text-muted-foreground">
                  {t('stats.dashboard.neverOpened', { learners: stats.neverOpened })}
                </p>
              )}
            />
          </div>

          {/*
            Illustration purement décorative : `alt` vide et `aria-hidden`, pour
            qu'un lecteur d'écran ne l'annonce pas — elle ne porte aucune donnée.
            Masquée sous `xl` : en dessous, elle mangerait la place du texte
            qu'elle est censée accompagner.

            ⚠️ Ancrée par le BAS. C'est ce qui garde le trait de coupe collé au
            bas du bandeau, donc masqué par les tuiles, quelle que soit la
            hauteur du bloc — et cette hauteur varie avec les données : la
            légende compte trois à cinq états, et deux notes apparaissent ou
            non. Ancrée par le haut, la coupe remontait au genou ou descendait
            sous les pieds selon l'organisation qui regarde l'écran.

            `bottom-8` et non `bottom-0` : la coupe tombe alors 32 px À
            L'INTÉRIEUR des tuiles, pas sur leur arête. Sur leur arête, le
            moindre arrondi de rendu laisse dépasser un liseré du personnage
            sous la rangée blanche.

            La fenêtre vaut ~55 % de la hauteur de l'illustration (ratio l/h
            0,471, mesuré sur le fichier recadré) : c'est ce qui coupe à la
            taille. Changer la largeur impose de recalculer la hauteur.

            Largeurs volontairement SOUS la résolution native (487 px) : au-delà,
            le navigateur agrandit un rendu déjà doux et le flou se voit.
          */}
          {showsIllustration && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute bottom-8 right-[3%] hidden h-[23rem] w-[20rem] overflow-hidden xl:block 2xl:h-[27rem] 2xl:w-[23rem]"
            >
              <img
                src="/chart-character.png"
                alt=""
                loading="lazy"
                className="w-full select-none"
              />
            </div>
          )}
        </div>
      </div>

      {/*
        Tuiles en chevauchement sur le bas du bandeau, comme la maquette. La
        marge négative doit rester inférieure au `pb` du bandeau, sinon elles
        remonteraient par-dessus la légende.
      */}
      <div className="relative -mt-20 grid gap-4 px-2 sm:grid-cols-3 sm:px-4" {...tourAnchor('dashboard.tiles')}>
        <StatTile {...tiles.successRate} />
        <StatTile {...tiles.averageScore} />
        <StatTile {...tiles.averageTime} />
      </div>
    </section>
  )
}

export default CompletionSummary
